import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Pen, Plus, BookOpen, Edit, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function WritingStylesPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Writing Styles</h1>
        <p className="text-lg text-gray-700 mb-6">
          Writing styles help you customize the voice and tone of your content. Choose from pre-built styles based on popular writers or create your own custom style.
        </p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="prebuilt">Pre-built Styles</TabsTrigger>
          <TabsTrigger value="custom">Custom Styles</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="pt-6">
          <div className="prose max-w-none">
            <h2>Writing Styles Overview</h2>
            <p>
              Writing styles are one of the most powerful features in AISEOBlogGenerator. They allow you to control not just what your content says, but how it says it.
            </p>
            
            <p>
              By selecting or creating a writing style, you can ensure that all your content maintains a consistent voice and tone that aligns with your brand identity.
            </p>
            
            <h3>Two Types of Writing Styles</h3>
            <ol>
              <li>
                <strong>Pre-built Styles</strong>: Based on popular authors and thought leaders like Simon Sinek, Naval Ravikant, and Morgan Housel.
              </li>
              <li>
                <strong>Custom Styles</strong>: Create your own styles with specific instructions for the AI to follow when generating content.
              </li>
            </ol>
            
            <div className="not-prose bg-yellow-50 p-4 border-l-4 border-yellow-400 my-6">
              <h4 className="text-yellow-800 font-medium">Responsible Use Notice</h4>
              <p className="text-sm text-yellow-700 mt-1">
                Pre-built styles are intended for inspiration and learning. They emulate the thought patterns and communication approaches of notable writers, but should be used ethically and creatively. Always maintain authenticity in your content and respect intellectual property rights.
              </p>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg p-6 mt-8">
            <h3 className="text-lg font-bold mb-4">How Writing Styles Work</h3>
            <p className="text-gray-700 mb-4">
              Writing styles work by providing the AI with specific instructions on how to structure sentences, what tone to use, what literary devices to employ, and how to approach the topic conceptually.
            </p>
            <div className="flex items-center justify-center mt-6">
              <img 
                src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?q=80&w=1200&auto=format&fit=crop" 
                alt="Writing process illustration" 
                className="rounded-lg shadow-md max-w-full max-h-72 object-cover"
              />
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="prebuilt" className="pt-6">
          <div className="prose max-w-none mb-6">
            <h2>Pre-built Writing Styles</h2>
            <p>
              Our platform includes a variety of pre-built writing styles inspired by popular authors and thought leaders.
              Each style has been crafted to capture the essence of how these individuals communicate their ideas.
            </p>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-white rounded-lg border p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="text-blue-600 w-5 h-5" />
                <h3 className="font-bold text-lg">Simon Sinek</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">
                Inspirational leadership insights with powerful storytelling
              </p>
              <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-700 font-mono">
                Start with 'why'. Focus on purpose-driven narratives that inspire action. Use compelling examples and analogies to illustrate key points.
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="text-blue-600 w-5 h-5" />
                <h3 className="font-bold text-lg">Naval Ravikant</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">
                Philosophical insights on wealth, happiness, and technology
              </p>
              <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-700 font-mono">
                Write in clear, concise principles. Focus on timeless wisdom and mental models. Use short, memorable statements followed by deeper explanations.
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="text-blue-600 w-5 h-5" />
                <h3 className="font-bold text-lg">Morgan Housel</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">
                Behavioral finance and psychology insights
              </p>
              <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-700 font-mono">
                Use storytelling to explain complex concepts. Draw unexpected connections between historical events and present-day insights. Focus on timeless principles of human behavior.
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="text-blue-600 w-5 h-5" />
                <h3 className="font-bold text-lg">Tim Urban</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">
                Deep dives with humor and visual thinking
              </p>
              <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-700 font-mono">
                Break down complex topics into digestible pieces. Use humor and analogies to maintain engagement. Think from first principles. Explain concepts as if teaching a smart friend.
              </div>
            </div>
          </div>
          
          <div className="mt-8 p-6 bg-gray-50 rounded-lg border">
            <h3 className="font-bold text-lg mb-3">How to Select a Pre-built Style</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex gap-3">
                <span className="flex-shrink-0 bg-blue-100 h-6 w-6 rounded-full flex items-center justify-center text-blue-800 font-bold">1</span>
                <span>Go to the <strong>Content Writer</strong> page</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 bg-blue-100 h-6 w-6 rounded-full flex items-center justify-center text-blue-800 font-bold">2</span>
                <span>Find the <strong>Writing Style</strong> dropdown in the Basic Options section</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 bg-blue-100 h-6 w-6 rounded-full flex items-center justify-center text-blue-800 font-bold">3</span>
                <span>Click the dropdown and scroll through the available styles</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 bg-blue-100 h-6 w-6 rounded-full flex items-center justify-center text-blue-800 font-bold">4</span>
                <span>Select the style that best matches your desired tone and approach</span>
              </li>
            </ol>
            <div className="mt-4 flex justify-center">
              <img 
                src="https://images.unsplash.com/photo-1456324504439-367cee3b3c32?q=80&w=1200&auto=format&fit=crop" 
                alt="Writing style selection" 
                className="rounded-md max-w-full max-h-48 object-cover"
              />
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="custom" className="pt-6">
          <div className="prose max-w-none mb-6">
            <h2>Custom Writing Styles</h2>
            <p>
              Create your own custom writing styles to match your brand voice perfectly or to experiment with different approaches to content creation.
            </p>
          </div>
          
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-bold mb-4">Creating a Custom Style</h3>
            <div className="space-y-6">
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <Plus className="w-4 h-4 text-blue-600" />
                  Method 1: During Content Creation
                </h4>
                <ol className="space-y-2 text-gray-700 ml-6 list-decimal">
                  <li>Go to the Content Writer page</li>
                  <li>In the Writing Style dropdown, select "Custom Style"</li>
                  <li>A text area will appear for you to enter your custom instructions</li>
                  <li>When you generate content with this style, it will be saved for future use</li>
                </ol>
              </div>
              
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <Pen className="w-4 h-4 text-blue-600" />
                  Method 2: Using the Styles Manager
                </h4>
                <ol className="space-y-2 text-gray-700 ml-6 list-decimal">
                  <li>Click the Settings button in the top navigation</li>
                  <li>Select "Show Writing Styles" from the dropdown menu</li>
                  <li>Click the "New Style" button in the styles panel</li>
                  <li>Fill in the name, description, and writing instructions</li>
                </ol>
              </div>
            </div>
            
            <div className="mt-8">
              <h4 className="font-medium mb-3">Custom Style Example</h4>
              <div className="border rounded-md bg-gray-50 p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Style Name</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border rounded-md bg-white" 
                    value="Technical Expert"
                    disabled
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border rounded-md bg-white" 
                    value="Clear and detailed technical explanations with practical examples"
                    disabled
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Writing Instructions</label>
                  <textarea 
                    className="w-full px-3 py-2 border rounded-md bg-white h-32 font-mono text-sm" 
                    disabled
                    value={`Write in a clear, authoritative tone that demonstrates deep technical knowledge. Use precise terminology but explain complex concepts with practical examples. Structure content logically with a progressive reveal of information. Include code snippets or step-by-step instructions where appropriate. Maintain a helpful, educational tone without being condescending. Address common pitfalls and edge cases.`}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-gray-50 border rounded-lg p-6">
            <h3 className="text-lg font-bold mb-4">Tips for Effective Custom Styles</h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-1 rounded-full">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-base">Be Specific About Structure</h4>
                  <p className="text-sm text-gray-600">
                    Include guidance on paragraph length, sentence structure, and section organization.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-1 rounded-full">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-base">Define Tone Clearly</h4>
                  <p className="text-sm text-gray-600">
                    Be explicit about emotional tone: professional, conversational, humorous, authoritative, etc.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-1 rounded-full">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-base">Specify Literary Devices</h4>
                  <p className="text-sm text-gray-600">
                    Mention if you want analogies, metaphors, stories, or specific rhetorical techniques.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-1 rounded-full">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-base">Include Formatting Preferences</h4>
                  <p className="text-sm text-gray-600">
                    Specify how you want headings, bullets, numbering, and other formatting elements to be used.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h4 className="font-medium text-base mb-2">Example Writing Instruction</h4>
              <div className="bg-white p-4 border rounded-md font-mono text-xs text-gray-800">
                Write with an authoritative but approachable voice, like an experienced mentor. Use short paragraphs (2-3 sentences) and frequent subheadings to improve readability. Start with a personal anecdote to engage readers, then transition to practical advice. Include real-world examples to illustrate each point. Conclude with actionable takeaways that the reader can implement immediately. Avoid jargon without explanation, and use metaphors to simplify complex concepts.
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="bg-white border rounded-lg p-6 mt-8">
        <h2 className="text-xl font-bold mb-4">Writing Styles Manager</h2>
        <div className="bg-gray-50 border rounded-md">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Pen className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold">Your Custom Styles</h3>
            </div>
            <Button variant="outline" size="sm" disabled className="flex items-center gap-1">
              <Plus className="w-4 h-4" />
              New Style
            </Button>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-white border rounded p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Conversational Expert</h4>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled>
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">Friendly, approachable content that explains complex topics simply</p>
              <div className="bg-gray-50 p-2 rounded text-xs font-mono text-gray-700">
                Write in a friendly, conversational tone that makes readers feel like they're talking to a knowledgeable friend...
              </div>
            </div>
            
            <div className="bg-white border rounded p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Data-Driven Marketer</h4>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled>
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">Analytical content with statistics and evidence-based insights</p>
              <div className="bg-gray-50 p-2 rounded text-xs font-mono text-gray-700">
                Focus on data and evidence. Include statistics to support all claims. Structure content with clear hypotheses...
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-500">
          <p>
            <strong>Note:</strong> To open the Writing Styles Manager, click the Settings button in the header and select "Show Writing Styles".
          </p>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/writer/marketing-options">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Marketing Options
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/writer/ai-models">
            Next: AI Models
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}